import { useCallback, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

const nonNegativeClamp = (value: number) => Math.max(0, value);

type OverflowBox = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

const readOverflowBox = (el: HTMLElement): OverflowBox => ({
  top: nonNegativeClamp(Math.floor(el.scrollTop)),
  right: nonNegativeClamp(
    Math.floor(el.scrollWidth - el.clientWidth - el.scrollLeft),
  ),
  bottom: nonNegativeClamp(
    Math.floor(el.scrollHeight - el.clientHeight - el.scrollTop),
  ),
  left: nonNegativeClamp(Math.floor(el.scrollLeft)),
});

const isSameOverflowBox = (a: OverflowBox, b: OverflowBox) =>
  a.top === b.top &&
  a.right === b.right &&
  a.bottom === b.bottom &&
  a.left === b.left;

export type UseOverflowBoxParam<ElementType extends HTMLElement> = {
  overflowBoxRef: RefObject<ElementType>;
};

export const useOverflowBox = <ElementType extends HTMLElement>({
  overflowBoxRef,
}: UseOverflowBoxParam<ElementType>) => {
  const [overflowBox, setOverflowBox] = useState<OverflowBox>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  const updateOverflowBox = useCallback(() => {
    const el = overflowBoxRef.current;
    if (!el) return;

    const next = readOverflowBox(el);
    setOverflowBox((prev) => (isSameOverflowBox(prev, next) ? prev : next));
  }, [overflowBoxRef]);

  useLayoutEffect(() => {
    const el = overflowBoxRef.current;
    if (!el) return;

    updateOverflowBox();

    const ro = new ResizeObserver(() => {
      updateOverflowBox();
    });

    const observeScrollContent = () => {
      ro.observe(el);
      for (const child of el.children) {
        if (child instanceof HTMLElement) {
          ro.observe(child);
        }
      }
    };

    observeScrollContent();

    const mo = new MutationObserver(() => {
      ro.disconnect();
      observeScrollContent();
      updateOverflowBox();
    });

    mo.observe(el, { childList: true });

    const postLayoutRaf = requestAnimationFrame(() => {
      updateOverflowBox();
    });

    return () => {
      cancelAnimationFrame(postLayoutRaf);
      mo.disconnect();
      ro.disconnect();
    };
  }, [overflowBoxRef, updateOverflowBox]);

  const hasOverflow =
    overflowBox.top ||
    overflowBox.bottom ||
    overflowBox.left ||
    overflowBox.right;

  return {
    overflowBoxRef,
    updateOverflowBox,
    hasOverflow,
    overflowBox,
  };
};
